import React, { useEffect, useState } from 'react';
import { Sparkles, ListChecks, BarChart3, ChevronLeft, Grid3x3, GitCompareArrows, Briefcase, Send, Loader2, Lock } from 'lucide-react';
import { atlasApi, AtlasUser } from '../shared/atlasApi';
import Questionario from './Questionario';
import RelatorioIndividual from './RelatorioIndividual';
import Comparativo from './Comparativo';
import MapaTime from './MapaTime';
import CompararDupla from './CompararDupla';
import PerfisCargo from './PerfisCargo';
import Solicitacoes from './Solicitacoes';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
  onAbrirFicha: (funccodi: number) => void;
}

type DiscView = 'home' | 'solicitacoes' | 'questionario' | 'relatorio' | 'time' | 'mapa' | 'comparar' | 'cargos';

const Card: React.FC<{
  icon: React.ReactNode;
  titulo: string;
  descricao: string;
  cor: string;
  onClick: () => void;
}> = ({ icon, titulo, descricao, cor, onClick }) => (
  <button
    onClick={onClick}
    className="group text-left bg-white rounded-2xl border border-gray-200 p-6 hover:border-purple-300 hover:shadow-md transition flex flex-col gap-3"
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white ${cor}`}>{icon}</div>
    <div>
      <h3 className="font-bold text-gray-900 group-hover:text-purple-700 transition">{titulo}</h3>
      <p className="text-sm text-gray-500 mt-1">{descricao}</p>
    </div>
  </button>
);

const DiscApp: React.FC<Props> = ({ user, podeEditar, onAbrirFicha }) => {
  const [view, setView] = useState<DiscView>('home');
  // Resultado de DISC: RH vê todos, gestor vê os liderados, o colaborador não vê nem o próprio.
  const [liderados, setLiderados] = useState<number | null>(null);

  useEffect(() => {
    if (podeEditar) return;
    let cancel = false;
    atlasApi.discAcesso(user)
      .then(r => { if (!cancel) setLiderados(r.liderados || 0); })
      .catch(() => { if (!cancel) setLiderados(0); });
    return () => { cancel = true; };
  }, [user, podeEditar]);

  const ehGestor = !podeEditar && !!liderados;
  const veTime = podeEditar || ehGestor;

  if (view === 'solicitacoes' && podeEditar) {
    return (
      <div className="p-6">
        <button onClick={() => setView('home')} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1 mb-4">
          <ChevronLeft className="w-4 h-4" /> Voltar
        </button>
        <Solicitacoes user={user} onAbrirFicha={onAbrirFicha} />
      </div>
    );
  }

  if (view === 'questionario' && podeEditar) {
    return (
      <div className="p-6">
        <button onClick={() => setView('home')} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1 mb-4">
          <ChevronLeft className="w-4 h-4" /> Voltar
        </button>
        <Questionario user={user} podeEditar={podeEditar} onConcluir={fc => (fc !== undefined ? onAbrirFicha(fc) : setView('relatorio'))} onCancelar={() => setView('home')} />
      </div>
    );
  }

  if (view === 'relatorio' && podeEditar) {
    return (
      <div className="p-6">
        <button onClick={() => setView('home')} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1 mb-4">
          <ChevronLeft className="w-4 h-4" /> Voltar
        </button>
        <RelatorioIndividual user={user} onRefazer={() => setView('questionario')} />
      </div>
    );
  }

  if (view === 'time') {
    return (
      <div className="p-6">
        <button onClick={() => setView('home')} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1 mb-4">
          <ChevronLeft className="w-4 h-4" /> Voltar
        </button>
        <Comparativo user={user} onAbrir={onAbrirFicha} />
      </div>
    );
  }

  if (view === 'mapa') {
    return (
      <div className="p-6">
        <button onClick={() => setView('home')} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1 mb-4">
          <ChevronLeft className="w-4 h-4" /> Voltar
        </button>
        <MapaTime user={user} onAbrir={onAbrirFicha} />
      </div>
    );
  }

  if (view === 'comparar') {
    return (
      <div className="p-6">
        <button onClick={() => setView('home')} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1 mb-4">
          <ChevronLeft className="w-4 h-4" /> Voltar
        </button>
        <CompararDupla user={user} />
      </div>
    );
  }

  if (view === 'cargos') {
    return (
      <div className="p-6">
        <button onClick={() => setView('home')} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1 mb-4">
          <ChevronLeft className="w-4 h-4" /> Voltar
        </button>
        <PerfisCargo user={user} podeEditar={podeEditar} />
      </div>
    );
  }

  if (!podeEditar && liderados === null) {
    return (
      <div className="p-12 text-center text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-2 text-purple-100 text-sm font-medium">
          <Sparkles className="w-4 h-4" /> Perfil comportamental
        </div>
        <h2 className="text-2xl font-bold mt-1">DISC</h2>
        <p className="text-purple-100 text-sm mt-1 max-w-xl">
          {podeEditar
            ? 'Mande o link do questionário para o colaborador responder. O resultado volta para cá e só o RH e o gestor veem.'
            : ehGestor
              ? 'Perfis de Dominância, Influência, Estabilidade e Conformidade da sua equipe.'
              : 'Mapeamento de Dominância, Influência, Estabilidade e Conformidade.'}
        </p>
      </div>

      {!veTime && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 flex gap-4">
          <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">O questionário chega pelo RH</h3>
            <p className="text-sm text-gray-500 mt-1">
              Quando for a sua vez, o RH manda um link para você responder. O resultado fica com o RH e com a sua liderança.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {podeEditar && (
          <Card
            icon={<Send className="w-6 h-6" />}
            titulo="Solicitar DISC"
            descricao="Gere o link, mande para o colaborador e acompanhe quem já respondeu."
            cor="bg-purple-600"
            onClick={() => setView('solicitacoes')}
          />
        )}
        {podeEditar && (
          <Card
            icon={<ListChecks className="w-6 h-6" />}
            titulo="Responder aqui"
            descricao="Aplicar o questionário pelo sistema, em nome de um colaborador."
            cor="bg-fuchsia-500"
            onClick={() => setView('questionario')}
          />
        )}
        {podeEditar && (
          <Card
            icon={<Sparkles className="w-6 h-6" />}
            titulo="Meu perfil"
            descricao="Veja seu relatório com gráfico radar e histórico."
            cor="bg-emerald-500"
            onClick={() => setView('relatorio')}
          />
        )}
        {veTime && (
          <Card
            icon={<BarChart3 className="w-6 h-6" />}
            titulo="Comparativo do time"
            descricao={ehGestor ? 'Distribuição de estilos da sua equipe.' : 'Distribuição de estilos por departamento.'}
            cor="bg-amber-500"
            onClick={() => setView('time')}
          />
        )}
        {veTime && (
          <Card
            icon={<Grid3x3 className="w-6 h-6" />}
            titulo="Mapa do time"
            descricao="Heatmap de D/I/S/C por pessoa, célula a célula."
            cor="bg-rose-500"
            onClick={() => setView('mapa')}
          />
        )}
        {veTime && (
          <Card
            icon={<GitCompareArrows className="w-6 h-6" />}
            titulo="Comparar dupla"
            descricao="Compare dois perfis e veja dicas de convivência."
            cor="bg-sky-500"
            onClick={() => setView('comparar')}
          />
        )}
        {podeEditar && (
          <Card
            icon={<Briefcase className="w-6 h-6" />}
            titulo="Perfis de cargo"
            descricao="Defina o perfil DISC ideal de cada cargo."
            cor="bg-indigo-500"
            onClick={() => setView('cargos')}
          />
        )}
      </div>
    </div>
  );
};

export default DiscApp;
